<template>
  <div>
    <!-- ============================================================
         Dialog Thêm mới / Cập nhật Gói Học Phí (tutition_v3) – V2
         Sử dụng: <package-form-dialog ref="pkgFormDialog" @saved="fetchData" />
         Mở:     this.$refs.pkgFormDialog.openForCreate()
                 this.$refs.pkgFormDialog.openForEdit(id)
         ============================================================ -->
    <dialog-base
      :visible.sync="visible"
      :title="isEditMode ? 'Cập nhật gói học phí' : 'Thêm mới gói học phí'"
      size="lg"
      cancel-text="Hủy"
      confirm-text="Lưu lại"
      :confirm-loading="loading"
      :use-panel="false"
      top="6vh"
      @confirm="handleSubmit"
      @cancel="handleClose"
    >
      <!-- Nút mở hướng dẫn thao tác -->
      <div class="pfd-help-entry mb-3">
        <el-tooltip content="Xem hướng dẫn chi tiết cách thiết lập và gia hạn gói học phí" placement="top">
          <el-button type="primary" icon="el-icon-question" plain size="small" @click="openGuideDialog">
            Hướng dẫn thiết lập gói
          </el-button>
        </el-tooltip>
      </div>

      <el-form
        ref="dataForm"
        :model="dataForm"
        :rules="formRules"
        label-width="210px"
        label-position="left"
        class="sk-dialog-content"
        v-loading="formLoading"
      >
        <!-- ══════════════════════════════════════════════════════
             PHÂN KHU 1 – THÔNG TIN CHUNG CỦA GÓI
             ══════════════════════════════════════════════════════ -->
        <div class="sk-panel mb-4">
          <div class="sk-panel-head">
            <div class="sk-panel-icon"><i class="el-icon-collection" /></div>
            <div class="pfd-panel-text">
              <p class="sk-panel-title">Thông tin chung của gói</p>
              <p class="sk-panel-subtitle">
                Mỗi học sinh có chu kỳ học phí riêng bắt đầu từ ngày học sinh tham gia vào gói.
              </p>
            </div>
          </div>

          <el-row :gutter="20">
            <!-- Tên gói -->
            <el-col :span="24">
              <el-form-item prop="name">
                <template v-slot:label>
                  Tên gói học phí
                  <el-tooltip content="Tên hiển thị của gói học phí (ví dụ: Gói học phí bán trú 6 tháng, Gói tiếng Anh tăng cường...)" placement="top">
                    <i class="el-icon-question pfd-help-icon ml-2" />
                  </el-tooltip>
                </template>
                <el-input
                  v-model="dataForm.name"
                  placeholder="Nhập tên gói học phí..."
                  maxlength="255"
                  show-word-limit
                  clearable
                />
              </el-form-item>
            </el-col>

            <!-- Số lượng học sinh tối đa -->
            <el-col :span="12">
              <el-form-item prop="maxMembers">
                <template v-slot:label>
                  Số học sinh tối đa
                  <el-tooltip content="Giới hạn số học sinh tối đa có thể tham gia gói này. Để trống nếu không giới hạn sĩ số." placement="top">
                    <i class="el-icon-question pfd-help-icon ml-2" />
                  </el-tooltip>
                </template>
                <el-input-number
                  v-model="dataForm.maxMembers"
                  :min="1"
                  :step="1"
                  placeholder="Không giới hạn"
                  style="width: 100%"
                  controls-position="right"
                />
              </el-form-item>
            </el-col>

            <!-- Mô tả -->
            <el-col :span="24">
              <el-form-item prop="description">
                <template v-slot:label>
                  Mô tả
                  <el-tooltip content="Ghi chú ngắn về đối tượng áp dụng, quyền lợi hoặc điều kiện của gói học phí (tối đa 500 ký tự)" placement="top">
                    <i class="el-icon-question pfd-help-icon ml-2" />
                  </el-tooltip>
                </template>
                <el-input
                  v-model="dataForm.description"
                  type="textarea"
                  :rows="3"
                  placeholder="Nhập mô tả gói học phí, ví dụ: Áp dụng cho học sinh khối Mầm non, bao gồm tiền ăn và học phí hè..."
                  maxlength="500"
                  show-word-limit
                />
              </el-form-item>
            </el-col>
          </el-row>
        </div>

        <!-- ══════════════════════════════════════════════════════
             PHÂN KHU 2 – CÁCH TÍNH VÀ THU HỌC PHÍ
             ══════════════════════════════════════════════════════ -->
        <div class="sk-panel mb-4">
          <div class="sk-panel-head">
            <div class="sk-panel-icon"><i class="el-icon-money" /></div>
            <div class="pfd-panel-text">
              <p class="sk-panel-title">Cách tính và thu học phí</p>
              <p class="sk-panel-subtitle">Thiết lập đơn vị tính và số lượng đơn vị cho mỗi lần thu học phí</p>
            </div>
          </div>

          <el-row :gutter="20">
            <!-- Đơn vị tính học phí -->
            <el-col :span="12">
              <el-form-item prop="billingCycle">
                <template v-slot:label>
                  Đơn vị tính học phí
                  <el-tooltip content="Chọn đơn vị dùng để tính học phí. Ví dụ chọn Hàng tháng nghĩa là học phí được tính theo tháng." placement="top">
                    <i class="el-icon-question pfd-help-icon ml-2" />
                  </el-tooltip>
                </template>
                <el-select
                  v-model="dataForm.billingCycle"
                  style="width: 100%"
                  @change="handleBillingCycleChange"
                >
                  <el-option label="Hàng tháng" value="MONTHLY" />
                  <el-option label="Hàng năm" value="YEARLY" />
                  <el-option label="Hàng ngày" value="DAILY" />
                  <el-option label="Thu một lần (không lặp lại)" value="ONE_TIME" />
                </el-select>
              </el-form-item>
            </el-col>

            <!-- Số đơn vị thu mỗi lần -->
            <el-col :span="12" v-if="['MONTHLY', 'YEARLY', 'DAILY'].includes(dataForm.billingCycle)">
              <el-form-item prop="cycleDuration">
                <template v-slot:label>
                  Số đơn vị thu mỗi lần
                  <el-tooltip content="Số đơn vị được thu trong một lần. Ví dụ Hàng tháng và số 3 nghĩa là mỗi lần thu 3 tháng." placement="top">
                    <i class="el-icon-question pfd-help-icon ml-2" />
                  </el-tooltip>
                </template>
                <el-input-number
                  v-model="dataForm.cycleDuration"
                  :min="1"
                  :step="1"
                  style="width: 100%"
                />
              </el-form-item>
            </el-col>
          </el-row>

          <!-- Banner diễn giải động cách thu học phí -->
          <div class="pfd-preview-banner pfd-preview-banner--billing mt-2">
            <i class="el-icon-info mr-1" />
            <span class="pfd-preview-label">Diễn giải cách thu:</span>
            <strong class="pfd-preview-text ml-1">{{ billingSummaryText }}</strong>
          </div>

          <!-- Diễn giải ví dụ chu kỳ theo học sinh -->
          <div class="pfd-section-desc mt-2" v-if="dataForm.billingCycle !== 'ONE_TIME'">
            <i class="el-icon-user mr-1" />
            <strong>Ví dụ:</strong> Nếu học sinh tham gia gói vào ngày <strong>01/08/2026</strong>,
            chu kỳ đầu tiên sẽ là
            <strong>01/08/2026 → {{ exampleCycleEndText }}</strong>. Chu kỳ tiếp theo bắt đầu ngay ngày hôm sau khi chu kỳ hiện tại kết thúc.
          </div>
        </div>

        <!-- ══════════════════════════════════════════════════════
             PHÂN KHU 3 – TỰ ĐỘNG GIA HẠN KHI HẾT HẠN
             ══════════════════════════════════════════════════════ -->
        <div class="sk-panel mb-4">
          <div class="sk-panel-head">
            <div class="sk-panel-icon"><i class="el-icon-refresh" /></div>
            <div class="pfd-panel-text">
              <p class="sk-panel-title">Tự động gia hạn khi hết hạn</p>
              <p class="sk-panel-subtitle">Cấu hình nhắc hạn và gia hạn tự động (chỉ áp dụng cho học sinh được đăng ký)</p>
            </div>
            <div v-if="dataForm.billingCycle === 'ONE_TIME'" class="pfd-locked-badge">
              <i class="el-icon-lock" /> Đã khóa (Gói "Thu một lần" không áp dụng gia hạn)
            </div>
          </div>

          <!-- Diễn giải quy tắc đăng ký gia hạn -->
          <div class="pfd-section-desc mb-3">
            <i class="el-icon-info mr-1 text-primary" />
            Khi bật, hệ thống tự động gia hạn theo đúng cách tính học phí của gói. Mỗi lần gia hạn sẽ tạo một chu kỳ mới. Nhân viên cần chọn học sinh được gia hạn trong danh sách riêng.
          </div>

          <el-row :gutter="20">
            <!-- Toggle autoRenew -->
            <el-col :span="12">
              <el-form-item prop="autoRenew">
                <template v-slot:label>
                  Cho phép tự động gia hạn
                  <el-tooltip content="Bật chính sách gia hạn cho gói. Nhân viên vẫn phải chọn học sinh được phép gia hạn ở danh sách riêng." placement="top">
                    <i class="el-icon-question pfd-help-icon ml-2" />
                  </el-tooltip>
                </template>
                <el-switch
                  v-model="dataForm.autoRenew"
                  :disabled="dataForm.billingCycle === 'ONE_TIME'"
                  active-text="Bật"
                  inactive-text="Tắt"
                  @change="handleAutoRenewChange"
                />
              </el-form-item>
            </el-col>

            <!-- Nhắc lần đầu trước hạn -->
            <el-col :span="12">
              <el-form-item prop="renewalNoticeDays">
                <template v-slot:label>
                  Nhắc lần đầu trước hạn (ngày)
                  <el-tooltip content="Số ngày trước khi hết hạn sẽ bắt đầu nhắc. Ví dụ: 10 nghĩa là nhắc trước 10 ngày khi chu kỳ sắp hết hạn." placement="top">
                    <i class="el-icon-question pfd-help-icon ml-2" />
                  </el-tooltip>
                </template>
                <el-input-number
                  v-model="dataForm.renewalNoticeDays"
                  :min="0"
                  :step="1"
                  style="width: 100%"
                  :disabled="dataForm.billingCycle === 'ONE_TIME' || !dataForm.autoRenew"
                />
              </el-form-item>
            </el-col>

            <!-- Nhắc lại mỗi -->
            <el-col :span="12">
              <el-form-item prop="renewalReminderFrequencyDays">
                <template v-slot:label>
                  Nhắc lại mỗi (ngày/lần)
                  <el-tooltip content="Sau lần nhắc đầu, hệ thống sẽ tiếp tục nhắc lại theo tần suất này. Ví dụ: 3 nghĩa là nhắc lại mỗi 3 ngày. Để trống nếu chỉ nhắc một lần." placement="top">
                    <i class="el-icon-question pfd-help-icon ml-2" />
                  </el-tooltip>
                </template>
                <el-input-number
                  v-model="dataForm.renewalReminderFrequencyDays"
                  :min="1"
                  :step="1"
                  :placeholder="0"
                  style="width: 100%"
                  :disabled="dataForm.billingCycle === 'ONE_TIME' || !dataForm.autoRenew"
                />
              </el-form-item>
            </el-col>
          </el-row>

          <!-- Ví dụ lịch nhắc hạn -->
          <div
            class="pfd-reminder-example"
            v-if="dataForm.autoRenew && dataForm.renewalNoticeDays > 0 && dataForm.billingCycle !== 'ONE_TIME'"
          >
            <i class="el-icon-bell mr-1" />
            <strong>Ví dụ lịch nhắc:</strong>
            Nếu chu kỳ hết hạn ngày <strong>31/10/2026</strong>, nhắc lần đầu vào
            <strong>{{ exampleFirstReminderText }}</strong>
            <span v-if="dataForm.renewalReminderFrequencyDays >= 1">
              → nhắc tiếp mỗi <strong>{{ dataForm.renewalReminderFrequencyDays }} ngày/lần</strong> cho đến khi hết hạn
            </span>.
          </div>

          <!-- Banner diễn giải động chính sách gia hạn -->
          <div class="pfd-preview-banner pfd-preview-banner--renewal mt-2">
            <i class="el-icon-refresh mr-1" />
            <span class="pfd-preview-label">Chính sách gia hạn:</span>
            <strong class="pfd-preview-text ml-1">{{ renewalSummaryText }}</strong>
          </div>
        </div>

        <!-- ══════════════════════════════════════════════════════
             PHÂN KHU 4 – TỔNG HỢP CẤU HÌNH GÓI
             ══════════════════════════════════════════════════════ -->
        <div class="pfd-summary-card mb-2">
          <div class="pfd-summary-title">
            <i class="el-icon-document-checked mr-1 text-primary" /> Tổng hợp cấu hình gói học phí
          </div>
          <div class="pfd-summary-grid">
            <div class="pfd-summary-item">
              <span class="pfd-summary-lbl">1. Cách tính & thu:</span>
              <span class="pfd-summary-val">{{ billingSummaryText }}</span>
            </div>
            <div class="pfd-summary-item">
              <span class="pfd-summary-lbl">2. Tự động gia hạn:</span>
              <span class="pfd-summary-val">{{ renewalSummaryText }}</span>
            </div>
            <div class="pfd-summary-item" v-if="dataForm.autoRenew && dataForm.renewalNoticeDays >= 0">
              <span class="pfd-summary-lbl">3. Nhắc hạn:</span>
              <span class="pfd-summary-val">{{ reminderSummaryText }}</span>
            </div>
          </div>
        </div>
      </el-form>
    </dialog-base>

    <!-- Dialog hướng dẫn chi tiết -->
    <package-guide-dialog ref="guideDialog" />
  </div>
</template>

<script>
import DialogBase from "@/components/DialogBase.vue";
import PackageGuideDialog from "@/components/tutition_v3/dialog/PackageGuideDialog.vue";
import PackageManagementService from "@/services/tutition_v3/PackageManagementService";
import NotifyMessage from "@/commonFuncion/NotifyMessage";
import moment from "moment";

export default {
  name: "PackageFormDialog",
  components: { DialogBase, PackageGuideDialog },

  data() {
    return {
      visible: false,
      loading: false,
      formLoading: false,
      isEditMode: false,
      editId: null,

      dataForm: {
        name: "",
        description: "",
        billingCycle: "MONTHLY",
        cycleDuration: 1,
        autoRenew: false,
        maxMembers: undefined,
        // Renewal config
        renewalNoticeDays: 0,
        renewalReminderFrequencyDays: null,
      },

      formRules: {
        name: [
          { required: true, message: "Vui lòng nhập tên gói học phí", trigger: "blur" },
          { min: 1, max: 255, message: "Tên gói tối đa 255 ký tự", trigger: "blur" },
        ],
        description: [
          { max: 500, message: "Mô tả tối đa 500 ký tự", trigger: "blur" },
        ],
        billingCycle: [
          { required: true, message: "Vui lòng chọn đơn vị tính học phí", trigger: "change" },
        ],
      },
    };
  },

  computed: {
    /** Diễn giải động cho Cách tính và thu học phí */
    billingSummaryText() {
      const cycle = this.dataForm.billingCycle;
      const dur = this.dataForm.cycleDuration || 1;
      if (cycle === "DAILY") {
        return dur > 1 ? `Thu học phí ${dur} ngày mỗi lần` : "Thu học phí theo từng ngày";
      }
      if (cycle === "MONTHLY") {
        return dur > 1 ? `Thu học phí ${dur} tháng mỗi lần` : "Thu học phí từng tháng";
      }
      if (cycle === "YEARLY") {
        return dur > 1 ? `Thu học phí ${dur} năm mỗi lần` : "Thu học phí 1 năm mỗi lần";
      }
      if (cycle === "ONE_TIME") {
        return "Chỉ thu một lần, không lặp lại";
      }
      return "—";
    },

    /** Diễn giải động cho Tự động gia hạn */
    renewalSummaryText() {
      if (this.dataForm.billingCycle === "ONE_TIME" || !this.dataForm.autoRenew) {
        return "Không tự động gia hạn. Nhân viên xử lý thủ công khi hết chu kỳ.";
      }
      const cycle = this.dataForm.billingCycle;
      const dur = this.dataForm.cycleDuration || 1;
      let unit = "tháng";
      if (cycle === "DAILY") unit = "ngày";
      else if (cycle === "YEARLY") unit = "năm";
      return `Khi hết chu kỳ, hệ thống tạo chu kỳ mới ${dur} ${unit} tiếp theo cho học sinh đã đăng ký gia hạn.`;
    },

    /** Diễn giải nhắc hạn */
    reminderSummaryText() {
      const noticeDays = this.dataForm.renewalNoticeDays || 0;
      const freq = this.dataForm.renewalReminderFrequencyDays;
      if (noticeDays === 0) return "Không nhắc trước hạn.";
      let text = `Nhắc trước ${noticeDays} ngày`;
      if (freq && freq >= 1) {
        text += `, nhắc lại mỗi ${freq} ngày`;
      }
      return text + ".";
    },

    /** Ví dụ ngày kết thúc chu kỳ đầu (từ 01/08/2026) */
    exampleCycleEndText() {
      const cycle = this.dataForm.billingCycle;
      const dur = this.dataForm.cycleDuration || 1;
      const start = moment("2026-08-01");
      let end;
      if (cycle === "MONTHLY") {
        end = start.clone().add(dur, "months").subtract(1, "days");
      } else if (cycle === "YEARLY") {
        end = start.clone().add(dur, "years").subtract(1, "days");
      } else if (cycle === "DAILY") {
        end = start.clone().add(dur - 1, "days");
      } else {
        return "—";
      }
      return end.format("DD/MM/YYYY");
    },

    /** Ví dụ ngày nhắc lần đầu (nếu chu kỳ hết hạn 31/10/2026) */
    exampleFirstReminderText() {
      const notice = this.dataForm.renewalNoticeDays || 0;
      if (notice <= 0) return "—";
      return moment("2026-10-31").subtract(notice, "days").format("DD/MM/YYYY");
    },
  },

  methods: {
    /** Mở dialog để tạo mới */
    openForCreate() {
      this.isEditMode = false;
      this.editId = null;
      this._resetForm();
      this.visible = true;
    },

    /** Mở dialog để chỉnh sửa – tải dữ liệu từ BE theo ID */
    async openForEdit(id) {
      this.isEditMode = true;
      this.editId = id;
      this._resetForm();
      this.visible = true;
      await this._loadById(id);
    },

    /** Tải chi tiết gói học phí theo ID */
    async _loadById(id) {
      this.formLoading = true;
      try {
        const res = await PackageManagementService.findById(id);
        const pkg = res.data.data;
        this.dataForm = {
          name: pkg.name || "",
          description: pkg.description || "",
          billingCycle: pkg.billingCycle || "MONTHLY",
          cycleDuration: pkg.cycleDuration || 1,
          autoRenew: pkg.autoRenew || false,
          maxMembers: pkg.maxMembers || undefined,
          // Renewal config
          renewalNoticeDays: pkg.renewalNoticeDays != null ? pkg.renewalNoticeDays : 0,
          renewalReminderFrequencyDays: pkg.renewalReminderFrequencyDays || null,
        };
      } catch (err) {
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Không thể tải thông tin gói học phí!"
        );
        this.visible = false;
      } finally {
        this.formLoading = false;
      }
    },

    /** Submit form (tạo mới hoặc cập nhật) */
    async handleSubmit() {
      const valid = await this.$refs.dataForm.validate().catch(() => false);
      if (!valid) return;

      const isOneTime = this.dataForm.billingCycle === "ONE_TIME";
      const autoRenew = isOneTime ? false : this.dataForm.autoRenew;

      // Validation: cycleDuration >= 1
      if (!isOneTime && (!this.dataForm.cycleDuration || this.dataForm.cycleDuration < 1)) {
        NotifyMessage.leftCornerNotificationError("Số đơn vị thu mỗi lần phải từ 1 trở lên.");
        return;
      }

      // Validation: renewalNoticeDays >= 0
      if (autoRenew && this.dataForm.renewalNoticeDays < 0) {
        NotifyMessage.leftCornerNotificationError("Số ngày nhắc trước hạn phải từ 0 trở lên.");
        return;
      }

      // Validation: renewalReminderFrequencyDays >= 1 nếu có
      if (autoRenew && this.dataForm.renewalReminderFrequencyDays != null && this.dataForm.renewalReminderFrequencyDays < 1) {
        NotifyMessage.leftCornerNotificationError("Tần suất nhắc lại phải từ 1 ngày trở lên.");
        return;
      }

      this.loading = true;
      try {
        const payload = {
          name: this.dataForm.name.trim(),
          description: this.dataForm.description ? this.dataForm.description.trim() : null,
          billingCycle: this.dataForm.billingCycle,
          cycleDuration: !isOneTime ? (this.dataForm.cycleDuration || 1) : null,
          autoRenew,
          maxMembers: this.dataForm.maxMembers || null,
          // Renewal config – chỉ gửi khi autoRenew bật và không phải ONE_TIME
          renewalNoticeDays: autoRenew ? (this.dataForm.renewalNoticeDays ?? 0) : null,
          renewalReminderFrequencyDays: autoRenew ? (this.dataForm.renewalReminderFrequencyDays || null) : null,
        };

        if (this.isEditMode) {
          const res = await PackageManagementService.update(this.editId, payload);
          NotifyMessage.leftCornerNotificationSuccess(
            res.data.message || "Cập nhật gói học phí thành công!"
          );
        } else {
          const res = await PackageManagementService.create(payload);
          NotifyMessage.leftCornerNotificationSuccess(
            res.data.message || "Thêm mới gói học phí thành công!"
          );
        }

        this.$emit("saved");
        this.handleClose();
      } catch (err) {
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Có lỗi xảy ra, vui lòng thử lại."
        );
      } finally {
        this.loading = false;
      }
    },

    /** Xử lý khi thay đổi đơn vị tính học phí */
    handleBillingCycleChange(val) {
      if (val === "ONE_TIME") {
        this.dataForm.autoRenew = false;
        this.dataForm.renewalNoticeDays = 0;
        this.dataForm.renewalReminderFrequencyDays = null;
      }
    },

    /** Xử lý khi bật/tắt tự động gia hạn */
    handleAutoRenewChange(val) {
      if (!val) {
        this.dataForm.renewalNoticeDays = 0;
        this.dataForm.renewalReminderFrequencyDays = null;
      }
    },

    /** Helper label đơn vị */
    getCycleLabel(cycle) {
      const map = {
        DAILY: "Hàng ngày",
        MONTHLY: "Hàng tháng",
        YEARLY: "Hàng năm",
        ONE_TIME: "Thu một lần",
      };
      return map[cycle] || cycle || "—";
    },

    /** Mở dialog hướng dẫn */
    openGuideDialog() {
      this.$refs.guideDialog && this.$refs.guideDialog.open();
    },

    /** Đóng dialog và reset form */
    handleClose() {
      this._resetForm();
      this.visible = false;
      this.$emit("closed");
    },

    _resetForm() {
      this.dataForm = {
        name: "",
        description: "",
        billingCycle: "MONTHLY",
        cycleDuration: 1,
        autoRenew: false,
        maxMembers: undefined,
        renewalNoticeDays: 0,
        renewalReminderFrequencyDays: null,
      };
      this.$nextTick(() => {
        this.$refs.dataForm && this.$refs.dataForm.resetFields();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/style/sk-dialog";

/* Panel text wrapper */
.pfd-panel-text {
  flex: 1;
  min-width: 0;
  word-break: break-word;
  overflow-wrap: break-word;

  .sk-panel-title,
  .sk-panel-subtitle {
    word-break: break-word;
    overflow-wrap: break-word;
    white-space: normal;
  }
}

/* Form Item Label & Content auto wrap */
/deep/ .el-form-item__label {
  line-height: 1.4 !important;
  min-height: 36px;
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  white-space: normal !important;
  word-break: break-word !important;
  overflow-wrap: break-word !important;
  padding-top: 4px;
  padding-bottom: 4px;
}

/deep/ .el-form-item__content {
  word-break: break-word;
  overflow-wrap: break-word;
}

/* Entry button mở hướng dẫn */
.pfd-help-entry {
  display: flex;
  justify-content: flex-end;
}

/* Icon help – giống pattern TotalRevenueFormDialog */
.pfd-help-icon {
  color: #64748b;
  font-size: 14px;
  vertical-align: middle;
  flex-shrink: 0;
}

/* Text giải thích dưới tiêu đề section */
.pfd-section-desc {
  font-size: 12.5px;
  line-height: 1.5;
  color: #475569;
  background: #f8fafc;
  border-left: 3px solid #2563eb;
  padding: 8px 12px;
  border-radius: 0 8px 8px 0;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

/* Ví dụ lịch nhắc hạn */
.pfd-reminder-example {
  margin-top: 10px;
  padding: 8px 12px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  font-size: 12.5px;
  color: #92400e;
  line-height: 1.5;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

/* Banner diễn giải động */
.pfd-preview-banner {
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.5;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;

  &--billing {
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    color: #166534;
  }

  &--renewal {
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    color: #1e40af;
  }
}

.pfd-preview-label {
  font-weight: 600;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

.pfd-preview-text {
  font-weight: 700;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

/* Card tổng hợp cấu hình */
.pfd-summary-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 16px;
  word-break: break-word;
  overflow-wrap: break-word;
}

.pfd-summary-title {
  font-size: 13.5px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

.pfd-summary-grid {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.pfd-summary-item {
  display: flex;
  align-items: baseline;
  font-size: 12.5px;
  line-height: 1.5;
  gap: 6px;
  flex-wrap: wrap;
}

.pfd-summary-lbl {
  font-weight: 600;
  color: #64748b;
  min-width: 130px;
  flex-shrink: 0;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

.pfd-summary-val {
  font-weight: 600;
  color: #0f172a;
  flex: 1;
  min-width: 0;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

/* Badge khóa renewal khi ONE_TIME */
.pfd-locked-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  color: #92400e;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  flex-shrink: 0;
  i { font-size: 13px; flex-shrink: 0; }
}
</style>
